/**
 * The optional modules and the one convention that gates them (piece 27,
 * Optional modules; decision 0520). E-Signature, Budgets and Depreciation are
 * each a `SettingValue` row defaulting to false, edited on Settings › Modules.
 *
 * 🔨 A disabled module hides its navigation rows, its Settings entries and its
 * reports, and leaves nothing behind. 🔨 Hiding is presentation, never access
 * control: a person who types the route still reaches it and the route's own
 * guard is what refuses them. 🔨 Disabling deletes nothing and blocks nothing —
 * no workflow and no posting is ever refused because a module is off.
 */
import { BUDGETS_ENABLED_KEY, DEPRECIATION_ENABLED_KEY, E_SIGNATURE_ENABLED_KEY } from './settingsIndex';

/** The three keys, in the order the Modules screen draws its switches. */
export const OPTIONAL_MODULE_KEYS = [E_SIGNATURE_ENABLED_KEY, BUDGETS_ENABLED_KEY, DEPRECIATION_ENABLED_KEY] as const;
export type OptionalModuleKey = (typeof OPTIONAL_MODULE_KEYS)[number];

/** What each switch is called and what turning it on does, as lookup keys rather than literals. */
export const OPTIONAL_MODULE_STRINGS: Readonly<Record<OptionalModuleKey, { label: string; purpose: string }>> = {
  [E_SIGNATURE_ENABLED_KEY]: {
    label: 'E-Signature',
    purpose: 'Send a Quote for electronic signature. With this off, a returned signed copy is still recorded by hand.',
  },
  [BUDGETS_ENABLED_KEY]: {
    label: 'Budgets',
    purpose: 'One budgeted figure per account per period, and the budget-versus-actual report over them. A budget never gates spend.',
  },
  [DEPRECIATION_ENABLED_KEY]: {
    label: 'Depreciation',
    purpose: 'Straight-line and declining-balance schedules on the equipment the organization owns, each period reviewed before it posts.',
  },
};

export function isOptionalModuleKey(value: string): value is OptionalModuleKey {
  return (OPTIONAL_MODULE_KEYS as readonly string[]).includes(value);
}

/** The three switches as one answer, which every gating surface reads once. */
export type ModuleStates = Readonly<Record<OptionalModuleKey, boolean>>;

/** 🔨 An absent row reads as off, which is the default the piece states; a malformed one reads as off too. */
export function moduleStatesFrom(values: Readonly<Record<string, unknown>>): ModuleStates {
  const out: Record<string, boolean> = {};
  for (const key of OPTIONAL_MODULE_KEYS) out[key] = values[key] === true || values[key] === 'true';
  return out as ModuleStates;
}

/** Every switch off, which is what a caller with nothing read yet is holding. */
export const ALL_MODULES_OFF: ModuleStates = moduleStatesFrom({});

/**
 * 🔒 The one gating question, asked by the navigation bar, the Settings Index
 * and the reports list alike: a row with no `moduleKey` is not a module's and
 * always shows, and a row that names one shows while that module is on.
 */
export function moduleRowVisible(row: { moduleKey?: string | null }, states: ModuleStates): boolean {
  const key = row.moduleKey;
  if (key === null || key === undefined || key === '') return true;
  if (!isOptionalModuleKey(key)) throw new OptionalModuleError(`a row names the optional module '${key}', which is not one of the three`);
  return states[key];
}

export class OptionalModuleError extends Error {}

/** The rows of any registry a disabled module should hide, filtered in one pass. */
export function visibleModuleRows<R extends { moduleKey?: string | null }>(rows: readonly R[], states: ModuleStates): R[] {
  return rows.filter((row) => moduleRowVisible(row, states));
}

/**
 * 🔨 Disabling never blocks: a write path asks this and is always allowed. The
 * function exists so that a future caller reaching for a module gate on a save
 * finds the answer written down rather than inventing one.
 */
export function moduleBlocksWork(): false {
  return false;
}
